import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Target, Eye, Award, Users, Clock, Globe, CheckCircle2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import Navbar from '@/components/layout/Navbar';
import Footer from '@/components/layout/Footer';
import SEO from '@/components/shared/SEO';
import { getSiteSettings } from '@/lib/api';

const defaultMission = "To deliver reliable, high-quality hospitality products and procurement solutions that empower our clients to operate efficiently and enhance guest experiences. We are committed to understanding our customers' needs, maintaining consistent quality standards, and providing responsive, value-driven service that builds long-term partnerships.";
const defaultVision = "To become a leading and most trusted hospitality supply partner across India, recognized for our quality, innovation, and customer-centric approach, while continuously expanding our product range and service capabilities to support the evolving needs of the industry.";

const defaultWhyUsPoints = [
  'Premium quality products from trusted manufacturers',
  'Competitive wholesale pricing',
  'Dedicated account managers for personalized service',
  'Flexible payment terms for regular customers',
  'Same-day dispatch for urgent requirements',
  'Eco-friendly and sustainable product options',
];

const defaultShowcaseImages = [
  '/assets/images/about/showcase-toiletries.webp',
  '/assets/images/about/showcase-towels.webp',
  '/assets/images/about/showcase-eco-disposables.webp',
  '/assets/images/about/showcase-amenities.webp',
];

export default function AboutPage() {
  const [settings, setSettings] = useState(null);

  useEffect(() => {
    getSiteSettings().then(res => setSettings(res.data)).catch(() => {});
  }, []);

  const stats = [
    { value: settings?.years_of_experience || '12+', label: 'Years of Experience' },
    { value: settings?.happy_customers || '500+', label: 'Happy Customers' },
    { value: settings?.total_products || '800+', label: 'Products' },
    { value: '100%', label: 'Quality Assured' },
  ];

  const values = [
    { icon: Target, title: 'Quality First', desc: 'We never compromise on the quality of our products' },
    { icon: Users, title: 'Customer Focus', desc: 'Building lasting relationships with our clients' },
    { icon: Globe, title: 'Sustainability', desc: 'Committed to eco-friendly solutions' },
    { icon: Clock, title: 'Reliability', desc: 'On-time delivery, every time' },
  ];

  // Dynamic content with fallbacks
  const mission = settings?.mission_statement || defaultMission;
  const vision = settings?.vision_statement || defaultVision;
  const whyUsHeading = settings?.why_choose_us_heading || 'What Sets Us Apart';
  const whyUsDescription = settings?.why_choose_us_description || 'We understand the unique needs of the hospitality industry. Our commitment to quality, sustainability, and customer satisfaction makes us the preferred choice for hotels and restaurants across India.';
  const whyUsPoints = (settings?.why_choose_us_points && settings.why_choose_us_points.length > 0) ? settings.why_choose_us_points : defaultWhyUsPoints;
  const showcaseImages = (settings?.about_showcase_images && settings.about_showcase_images.filter(Boolean).length > 0) ? settings.about_showcase_images : defaultShowcaseImages;

  return (
    <div className="min-h-screen" data-testid="about-page">
      <SEO title="About Us" description="Learn about Shubha Lakshmi Enterprises - 12+ years of excellence in hospitality supplies. Trusted by 500+ customers across India." />
      <Navbar />

      {/* Hero Section */}
      <section className="pt-32 pb-20 bg-[#FAFAF9]" data-testid="about-hero">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="section-overline text-[#D4AF37]">About Us</span>
              <h1
                className="text-4xl md:text-5xl lg:text-6xl font-bold text-primary mt-4 leading-tight"
                style={{ fontFamily: 'Manrope' }}
              >
                {settings?.about_heading || 'Your Trusted Partner in Hospitality Supplies'}
              </h1>
              <p className="text-base text-muted-foreground mt-6 leading-relaxed">
                {settings?.about_description || 'Established in 2010 and headquartered in Jaipur, Rajasthan, Shubha Lakshmi Enterprises has grown into a trusted partner for institutional clients.'}
              </p>
              <p className="text-base text-muted-foreground mt-4 leading-relaxed">
                {settings?.about_description_2 || ''}
              </p>
              {settings?.about_description_3 && (
                <p className="text-base text-muted-foreground mt-4 leading-relaxed">
                  {settings.about_description_3}
                </p>
              )}
              {settings?.about_description_4 && (
                <p className="text-base text-muted-foreground mt-4 leading-relaxed">
                  {settings.about_description_4}
                </p>
              )}
              <div className="flex gap-4 mt-8">
                <Link to="/products">
                  <Button className="rounded-full bg-primary hover:bg-primary/90 px-8" data-testid="about-explore-btn">
                    Explore Products
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
                <Link to="/contact">
                  <Button variant="outline" className="rounded-full border-primary text-primary" data-testid="about-contact-btn">
                    Contact Us
                  </Button>
                </Link>
              </div>
            </div>
            <div className="relative">
              <img
                src={settings?.about_image_url || "/assets/images/about2.webp"}
                alt="Shubha Lakshmi Enterprises"
                className="w-full aspect-[4/3] object-cover rounded-2xl shadow-2xl"
              />
              <div className="absolute -bottom-6 -left-6 bg-white p-6 rounded-xl shadow-xl">
                <p className="text-4xl font-bold text-primary" style={{ fontFamily: 'Manrope' }}>{settings?.years_of_experience || '12+'}</p>
                <p className="text-sm text-muted-foreground">Years of Excellence</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-16 bg-primary" data-testid="about-stats">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, idx) => (
              <div key={idx} className="text-center">
                <p className="text-4xl md:text-5xl font-bold text-white" style={{ fontFamily: 'Manrope' }}>
                  {stat.value}
                </p>
                <p className="text-white/70 mt-2">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-24 md:py-32 bg-white" data-testid="about-mission">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid md:grid-cols-2 gap-12">
            <div className="bg-[#FAFAF9] p-10 rounded-2xl">
              <div className="w-14 h-14 bg-primary/10 rounded-2xl flex items-center justify-center mb-6">
                <Target className="w-7 h-7 text-primary" />
              </div>
              <h2 className="text-2xl font-bold text-primary mb-4" style={{ fontFamily: 'Manrope' }}>
                Our Mission
              </h2>
              <p className="text-muted-foreground leading-relaxed">
                {mission}
              </p>
            </div>
            <div className="bg-[#FAFAF9] p-10 rounded-2xl">
              <div className="w-14 h-14 bg-primary/10 rounded-2xl flex items-center justify-center mb-6">
                <Eye className="w-7 h-7 text-primary" />
              </div>
              <h2 className="text-2xl font-bold text-primary mb-4" style={{ fontFamily: 'Manrope' }}>
                Our Vision
              </h2>
              <p className="text-muted-foreground leading-relaxed">
                {vision}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-24 md:py-32 bg-[#FAFAF9]" data-testid="about-values">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <span className="section-overline">Our Foundation</span>
            <h2 className="section-title text-4xl md:text-5xl mt-4" style={{ fontFamily: 'Manrope' }}>
              Core Values
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, idx) => (
              <div key={idx} className="bg-white p-8 rounded-2xl text-center card-lift">
                <div className="w-16 h-16 bg-primary/10 rounded-2xl flex items-center justify-center mx-auto mb-6">
                  <value.icon className="w-8 h-8 text-primary" />
                </div>
                <h3 className="font-semibold text-primary text-lg mb-2" style={{ fontFamily: 'Manrope' }}>
                  {value.title}
                </h3>
                <p className="text-sm text-muted-foreground">{value.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-24 md:py-32 bg-[#FAFAF9]" data-testid="about-why-us">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="section-overline">Why Choose SLE</span>
              <h2 className="section-title text-4xl md:text-5xl mt-4" style={{ fontFamily: 'Manrope' }}>
                {whyUsHeading}
              </h2>
              <p className="text-muted-foreground mt-6 leading-relaxed">
                {whyUsDescription}
              </p>
              
              <ul className="mt-8 space-y-4">
                {whyUsPoints.map((item, idx) => (
                  <li key={idx} className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                    <span className="text-muted-foreground">{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="grid grid-cols-2 gap-4">
              {showcaseImages[0] && (
                <img
                  src={showcaseImages[0]}
                  alt="Hotel toiletries display"
                  className="rounded-xl h-48 w-full object-cover"
                />
              )}
              {showcaseImages[1] && (
                <img
                  src={showcaseImages[1]}
                  alt="Luxury hotel towels"
                  className="rounded-xl h-48 w-full object-cover mt-8"
                />
              )}
              {showcaseImages[2] && (
                <img
                  src={showcaseImages[2]}
                  alt="Eco-friendly disposables"
                  className="rounded-xl h-48 w-full object-cover -mt-8"
                />
              )}
              {showcaseImages[3] && (
                <img
                  src={showcaseImages[3]}
                  alt="Hotel amenities collection"
                  className="rounded-xl h-48 w-full object-cover"
                />
              )}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="cta-section py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center relative z-10">
          <h2 className="text-3xl md:text-5xl font-bold text-white mb-6" style={{ fontFamily: 'Manrope' }}>
            Let's Work Together
          </h2>
          <p className="text-white/80 text-lg mb-8 max-w-2xl mx-auto">
            Join 500+ satisfied clients who trust us for their hospitality supply needs.
          </p>
          <Link to="/contact">
            <Button
              size="lg"
              className="rounded-full bg-white text-primary hover:bg-white/90 px-8 py-6 font-semibold"
              data-testid="about-cta-btn"
            >
              Get in Touch
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
